/**
 * Smart/curly quote conversion for AI-generated text.
 *
 * AI models (GPT, Claude, etc.) produce text with straight quotes (" and ') because
 * of how their tokenizers work. But many users write with curly/smart quotes
 * (\u201C \u201D \u2018 \u2019), either by typing (TipTap's Typography extension
 * converts straight quotes to curly on keyboard input) or by preference.
 *
 * To respect the user's style, we detect which quote style dominates their current
 * document and convert AI-generated text to match. If the document has no quotes or
 * the count is tied, we default to curly quotes since they're the typographic standard.
 *
 * This module is used at every point where AI text enters the editor:
 * - Write card insertion and streaming (TiptapDocumentSB/index.js)
 * - Draft prose streaming (Draft/GeneratePanel/index.js)
 * - ProseGenerator streaming (ProseGenerator/index.js)
 * - Command Menu streaming (CommandMenu/index.js)
 * - Card display in the right sidebar (CardView.js, Favorites.js)
 * - HTML content insertion via insertEditor()
 */

// Unicode quote characters
const LEFT_DOUBLE_QUOTE = "\u201C"; // "
const RIGHT_DOUBLE_QUOTE = "\u201D"; // "
const LEFT_SINGLE_QUOTE = "\u2018"; // '
const RIGHT_SINGLE_QUOTE = "\u2019"; // '

/**
 * Detect whether the document predominantly uses curly or straight quotes.
 *
 * Scans the full document text and counts all quote characters — both single and
 * double, curly and straight. The goal is to determine the user's preference:
 *
 * - Curly characters counted: \u201C \u201D (double) and \u2018 \u2019 (single)
 * - Straight characters counted: " and '
 *
 * We count both single and double quotes together because the user's quote style
 * applies to both (a document using curly double quotes will also use curly
 * apostrophes/single quotes, and vice versa).
 *
 * Returns:
 * - "straight" if there are strictly more straight quotes than curly
 * - "curly" if curly quotes equal or outnumber straight (we preference curly on ties)
 * - "none" if the document contains no quote characters at all
 *
 * Callers treat "none" the same as "curly" — when there's no existing preference,
 * we default to curly since they're the typographic standard for published prose.
 *
 * @param {string} documentText - The full text content of the document
 * @returns {"curly" | "straight" | "none"} The detected quote style
 */
export function detectQuoteStyle(documentText) {
  if (!documentText) return "none";

  let curlyCount = 0;
  let straightCount = 0;

  for (let i = 0; i < documentText.length; i++) {
    const ch = documentText[i];
    if (
      ch === LEFT_DOUBLE_QUOTE ||
      ch === RIGHT_DOUBLE_QUOTE ||
      ch === LEFT_SINGLE_QUOTE ||
      ch === RIGHT_SINGLE_QUOTE
    ) {
      curlyCount++;
    } else if (ch === '"' || ch === "'") {
      straightCount++;
    }
  }

  if (curlyCount === 0 && straightCount === 0) return "none";
  if (straightCount > curlyCount) return "straight";
  return "curly";
}

/**
 * Characters that indicate the next quote should be an opening quote.
 * Includes whitespace, start-of-string, opening brackets, newlines, and em-dashes.
 */
const OPENING_CONTEXT_RE = /^$|[\s\n\r([{\u2014\u2013—–-]$/;

/**
 * Common English contraction suffixes that follow an apostrophe.
 * When a straight single quote is in "opening context" but followed by one of
 * these suffixes, it's an apostrophe (RIGHT single quote), not an opening quote.
 */
const CONTRACTION_SUFFIX_RE = /^'(ve|re|ll|s|d|m|t|em|cause|til|twas|tis|nough)\b/i;

/**
 * Convert straight quotes (" and ') to their curly/smart equivalents.
 *
 * Uses the preceding character to determine directionality:
 * - After whitespace, start-of-string, or opening punctuation → opening quote (\u201C or \u2018)
 * - After a letter or other character → closing quote / apostrophe (\u201D or \u2019)
 *
 * This means contractions like "don't" correctly get a right single quote (\u2019)
 * because the apostrophe follows a letter.
 *
 * Already-curly quotes are left unchanged (they don't match " or ').
 *
 * @param {string} text - Text containing straight quotes
 * @returns {string} Text with straight quotes replaced by curly quotes
 */
export function straightToCurly(text) {
  if (!text) return text;

  let result = "";

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const preceding = i === 0 ? "" : text[i - 1];

    if (ch === '"') {
      // Determine if this is an opening or closing double quote
      if (i === 0 || OPENING_CONTEXT_RE.test(preceding)) {
        // Special case: after a dash, look ahead to determine if this is
        // an interrupted-dialogue closer (followed by space/punctuation/end)
        // vs genuine new dialogue (followed by a word character)
        if (/[\u2014\u2013\u2012\u2015\u002D]/.test(preceding)) {
          const next = text[i + 1];
          if (!next || /[\s,.!?;:\u2014\u2013)}\]]/.test(next)) {
            result += RIGHT_DOUBLE_QUOTE;
          } else {
            result += LEFT_DOUBLE_QUOTE;
          }
        } else {
          result += LEFT_DOUBLE_QUOTE;
        }
      } else {
        result += RIGHT_DOUBLE_QUOTE;
      }
    } else if (ch === "'") {
      // Determine if this is an opening single quote or apostrophe/closing
      if (i === 0 || OPENING_CONTEXT_RE.test(preceding)) {
        // Check if this is actually a contraction suffix at a chunk boundary
        // e.g., streaming chunk "'ve been there" from "I" + "'ve been there"
        if (CONTRACTION_SUFFIX_RE.test(text.substring(i, i + 10))) {
          result += RIGHT_SINGLE_QUOTE;
        } else if (/[\u2014\u2013\u2012\u2015\u002D]/.test(preceding)) {
          // Same dash look-ahead for single quotes
          const next = text[i + 1];
          if (!next || /[\s,.!?;:\u2014\u2013)}\]]/.test(next)) {
            result += RIGHT_SINGLE_QUOTE;
          } else {
            result += LEFT_SINGLE_QUOTE;
          }
        } else {
          result += LEFT_SINGLE_QUOTE;
        }
      } else {
        result += RIGHT_SINGLE_QUOTE;
      }
    } else {
      // Leave all other characters unchanged (including already-curly quotes)
      result += ch;
    }
  }

  return result;
}

// Cache TTL in milliseconds — avoids calling getText() on every streaming chunk.
// During AI streaming, convertQuotes is called many times per second. Detecting
// the quote style requires scanning the full document text, so we cache the result
// on the TipTap editor instance for 60 seconds. Users don't change their quote
// style mid-session, so a long TTL is fine and reduces unnecessary getText() calls.
const CACHE_TTL = 60000;

/**
 * Resolve an editor reference to the actual TipTap editor instance.
 *
 * Different parts of the app pass different editor objects:
 * - TiptapDocumentSB uses `this.tiptap` (the raw TipTap editor) for streaming
 * - RightBarSubscribe passes `this.editor` (the Editor wrapper from lib/Editor.js)
 *   which has a `.tiptap` property containing the TipTap editor
 *
 * This function normalizes both to the raw TipTap editor so that getText() and
 * cache properties work consistently regardless of which object was passed.
 *
 * @param {object} editor - Either a TipTap editor or an Editor wrapper
 * @returns {object|null} The resolved TipTap editor instance, or null
 */
export function resolveEditor(editor) {
  if (!editor) return null;
  // If it has getText, it's already the TipTap editor
  if (typeof editor.getText === 'function') return editor;
  // If it has a tiptap property, use that (Editor wrapper pattern)
  if (editor.tiptap && typeof editor.tiptap.getText === 'function') return editor.tiptap;
  return null;
}

/**
 * Convert quotes in AI-generated text to match the user's document style.
 *
 * Detects the quote style from the current document (via editor.getText()),
 * caches the result for performance, and converts straight quotes to curly
 * if the document uses curly quotes (or has no quotes yet).
 *
 * If the document uses straight quotes, the text is returned unchanged —
 * AI models already produce straight quotes, so no conversion is needed.
 *
 * @param {string} text - The AI-generated text to convert
 * @param {object} editor - The TipTap editor instance or Editor wrapper
 * @returns {string} Text with quotes converted to match document style
 */
export function convertQuotes(text, editor) {
  if (!text) return text;

  const tiptap = resolveEditor(editor);
  let style;

  // Check editor cache first for performance during streaming
  if (tiptap && tiptap._cachedQuoteStyle && tiptap._cachedQuoteStyleExpiry > Date.now()) {
    style = tiptap._cachedQuoteStyle;
  } else if (tiptap) {
    try {
      const docText = tiptap.getText();
      style = detectQuoteStyle(docText);
    } catch (_) {
      // If getText() throws (editor destroyed, etc.), default to curly
      style = "none";
    }

    // Cache the result on the tiptap editor (not the wrapper)
    tiptap._cachedQuoteStyle = style;
    tiptap._cachedQuoteStyleExpiry = Date.now() + CACHE_TTL;
  } else {
    style = "none";
  }

  // If the document uses straight quotes, don't convert
  if (style === "straight") return text;

  // Otherwise (curly or none/empty), convert to curly
  return straightToCurly(text);
}

/**
 * Convert quotes in HTML content to match the user's document style.
 *
 * Same logic as convertQuotes, but HTML-safe: only converts quotes in text
 * content between tags, not inside tag attributes (where straight quotes are
 * part of the HTML syntax, e.g. <p class="test">).
 *
 * Used by insertEditor() when inserting HTML content from cards into the editor.
 *
 * @param {string} html - HTML string with potential straight quotes in text content
 * @param {object} editor - The TipTap editor instance or Editor wrapper
 * @returns {string} HTML with text-content quotes converted
 */
export function convertQuotesInHtml(html, editor) {
  if (!html) return html;

  const tiptap = resolveEditor(editor);
  let style;

  // Same detection logic as convertQuotes
  if (tiptap && tiptap._cachedQuoteStyle && tiptap._cachedQuoteStyleExpiry > Date.now()) {
    style = tiptap._cachedQuoteStyle;
  } else if (tiptap) {
    try {
      const docText = tiptap.getText();
      style = detectQuoteStyle(docText);
    } catch (_) {
      style = "none";
    }

    // Cache the result on the tiptap editor (not the wrapper)
    tiptap._cachedQuoteStyle = style;
    tiptap._cachedQuoteStyleExpiry = Date.now() + CACHE_TTL;
  } else {
    style = "none";
  }

  if (style === "straight") return html;

  // Convert only text content, not tag attributes
  // Match either HTML tags (group 1) or text between tags (group 2)
  return html.replace(/(<[^>]*>)|([^<]+)/g, (match, tag, textContent) => {
    if (tag) {
      // This is an HTML tag — leave it unchanged
      return tag;
    }
    // This is text content — convert quotes
    return straightToCurly(textContent);
  });
}
